import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, 
  XCircle, 
  Ban, 
  ShieldAlert, 
  Search, 
  Copy, 
  Download,
  MoreVertical,
  UserCheck,
  CheckSquare,
  Square,
  Eye,
  UserX,
  ShieldCheck,
  ShieldMinus
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { UserDetailsSheet } from "@/components/admin/user-details-sheet";
import { registrarLogAdmin } from "@/utils/admin-logs";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

export const Route = createFileRoute("/admin/usuarios")({
  component: UsuariosAdmin,
});

function UsuariosAdmin() {
  const { profile: adminProfile } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentTab, setCurrentTab] = useState("pendente");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const { data: usuarios, isLoading } = useQuery({
    queryKey: ["admin-users", currentTab, search],
    queryFn: async () => {
      let query = supabase
        .from("profiles")
        .select("*, favoritos_count:favoritos(count), aprovado_por_rel:profiles!aprovado_por(nome)")
        .eq("status", currentTab)
        .order("created_at", { ascending: false });

      if (search) {
        query = query.or(`nome.ilike.%${search}%,email.ilike.%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.map(u => ({
        ...u,
        favoritos_count: (u as any).favoritos_count?.[0]?.count || 0
      }));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const { data: before } = await supabase.from("profiles").select("*").in("id", ids);
      
      const updateData: any = { status };
      if (status === "aprovado") {
        updateData.aprovado_em = new Date().toISOString();
        updateData.aprovado_por = adminProfile?.id;
      }
      const { data, error, count } = await supabase
        .from("profiles")
        .update(updateData)
        .in("id", ids)
        .select();

      if (error) {
        console.error("Erro no update de status:", error);
        throw error;
      }

      // Log for each user
      if (adminProfile) {
        for (const id of ids) {
          const userBefore = before?.find(b => b.id === id);
          const userAfter = data?.find(a => a.id === id);
          await registrarLogAdmin({
            adminId: adminProfile.id,
            acao: `Alteração de status para ${status}`,
            tabela: "profiles",
            registroId: id,
            dadosAntes: userBefore,
            dadosDepois: userAfter
          });
        }
      }
      
      return { count: data?.length || 0 };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setSelectedIds([]);
      if (data.count === 0) {
        toast.warning("Nenhum registro foi alterado. Verifique as permissões.");
      } else {
        toast.success(`${data.count} usuário(s) atualizado(s) com sucesso!`);
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao atualizar usuários"}`);
    }
  });

  const roleMutation = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) => {
      if (!adminProfile) return;
      
      const { data: before } = await supabase.from("profiles").select("*").eq("id", id).single();
      
      const { data, error } = await supabase
        .from("profiles")
        .update({ role })
        .eq("id", id)
        .select();

      if (error) {
        console.error("Erro ao alterar papel:", error);
        throw error;
      }

      await registrarLogAdmin({
        adminId: adminProfile.id,
        acao: `Alteração de papel para ${role}`,
        tabela: "profiles",
        registroId: id,
        dadosAntes: before,
        dadosDepois: data?.[0]
      });

      return { success: (data?.length || 0) > 0 };
    },
    onSuccess: (data) => {
      if (!data?.success) {
        toast.warning("Não foi possível alterar o papel. Verifique as permissões.");
      } else {
        queryClient.invalidateQueries({ queryKey: ["admin-users"] });
        toast.success("Papel do usuário atualizado!");
      }
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message || "Falha ao alterar papel"}`);
    }
  });

  const handleSelectAll = () => {
    if (selectedIds.length === (usuarios?.length || 0)) {
      setSelectedIds([]);
    } else {
      setSelectedIds(usuarios?.map(u => u.id) || []);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const copyEmails = () => {
    const emails = usuarios
      ?.filter(u => selectedIds.includes(u.id))
      .map(u => u.email)
      .join(", ");
    if (emails) {
      navigator.clipboard.writeText(emails);
      toast.success("E-mails copiados!");
    }
  };

  const exportCSV = () => {
    if (!usuarios?.length) return;
    const headers = ["Nome", "Email", "Telefone", "Empresa", "Status", "Data"];
    const rows = usuarios.map(u => [
      u.nome,
      u.email,
      u.telefone || "",
      u.empresa || "",
      u.status,
      format(new Date(u.created_at!), "dd/MM/yyyy HH:mm")
    ]);
    const csvContent = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `usuarios_${currentTab}_${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
  };

  const openUserDetails = (id: string) => {
    setSelectedUserId(id);
    setIsSheetOpen(true);
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pendente: "bg-amber-100 text-amber-700",
      aprovado: "bg-green-100 text-green-700",
      nao_cliente: "bg-blue-100 text-blue-700",
      bloqueado: "bg-red-100 text-red-700",
    };
    return <Badge className={cn("capitalize shadow-none border-none", colors[status])}>{status.replace("_", " ")}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input 
            placeholder="Buscar por nome ou e-mail..." 
            className="pl-10"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          {selectedIds.length > 0 && (
            <>
              <Button variant="outline" size="sm" onClick={copyEmails}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar E-mails
              </Button>
              <Button 
                size="sm" 
                onClick={() => updateStatusMutation.mutate({ ids: selectedIds, status: "aprovado" })}
                disabled={currentTab === "aprovado" || updateStatusMutation.isPending}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Aprovar Selecionados
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={exportCSV}>
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab}>
        <TabsList className="bg-white border w-full md:w-auto justify-start h-auto p-1">
          <TabsTrigger value="pendente" className="data-[state=active]:bg-amber-50 data-[state=active]:text-amber-700">Pendentes</TabsTrigger>
          <TabsTrigger value="aprovado" className="data-[state=active]:bg-green-50 data-[state=active]:text-green-700">Aprovados</TabsTrigger>
          <TabsTrigger value="nao_cliente" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">Não-clientes</TabsTrigger>
          <TabsTrigger value="bloqueado" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700">Bloqueados</TabsTrigger>
        </TabsList>

        <div className="bg-white border rounded-xl overflow-hidden mt-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox 
                    checked={selectedIds.length > 0 && selectedIds.length === usuarios?.length}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail / Telefone</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead>Cadastro</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-400">Carregando usuários...</TableCell>
                </TableRow>
              ) : usuarios?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-gray-400">Nenhum usuário encontrado.</TableCell>
                </TableRow>
              ) : (
                usuarios?.map((u) => (
                  <TableRow 
                    key={u.id} 
                    className="cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      setSelectedUserId(u.id);
                      setIsSheetOpen(true);
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox 
                        checked={selectedIds.includes(u.id)}
                        onCheckedChange={() => toggleSelect(u.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium h-16">
                      <div className="flex items-center gap-2">
                        {u.nome}
                        {u.role === 'super_admin' && (
                          <Badge variant="outline" className="text-[10px] py-0 border-amber-200 text-amber-700 font-bold">Super Admin</Badge>
                        )}
                        {u.role === 'admin' && (
                          <Badge variant="outline" className="text-[10px] py-0 border-blue-200 text-blue-700 font-bold">Admin</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{u.email}</div>
                      <div className="text-xs text-gray-500 mt-1">{u.telefone || "-"}</div>
                    </TableCell>
                    <TableCell className="text-sm">{u.empresa || "-"}</TableCell>
                    <TableCell>{statusBadge(u.status)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      -
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {u.created_at ? format(new Date(u.created_at), "dd/MM/yy HH:mm") : "-"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                          <DropdownMenuItem onClick={() => openUserDetails(u.id)}>
                            <Eye className="w-4 h-4 mr-2" />
                            Ver e editar
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <DropdownMenuItem 
                                    className="text-green-600"
                                    onClick={() => updateStatusMutation.mutate({ ids: [u.id], status: "aprovado" })}
                                    disabled={u.status === "aprovado"}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Aprovar
                                  </DropdownMenuItem>
                                </div>
                              </TooltipTrigger>
                              {u.status === "aprovado" && (
                                <TooltipContent>Usuário já está aprovado</TooltipContent>
                              )}
                            </Tooltip>
                          </TooltipProvider>

                          <DropdownMenuItem 
                            className="text-blue-600"
                            onClick={() => updateStatusMutation.mutate({ ids: [u.id], status: "nao_cliente" })}
                            disabled={u.status === "nao_cliente"}
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            Marcar como Não-cliente
                          </DropdownMenuItem>

                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => updateStatusMutation.mutate({ ids: [u.id], status: "bloqueado" })}
                            disabled={u.status === "bloqueado"}
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Bloquear
                          </DropdownMenuItem>
                          
                          {adminProfile?.role === "super_admin" && (
                            <>
                              <DropdownMenuSeparator />
                              {u.role === "user" ? (
                                <DropdownMenuItem 
                                  className="text-amber-600 font-semibold"
                                  onClick={() => roleMutation.mutate({ id: u.id, role: "admin" })}
                                >
                                  <ShieldCheck className="w-4 h-4 mr-2" />
                                  Tornar Admin
                                </DropdownMenuItem>
                              ) : (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div>
                                        <DropdownMenuItem 
                                          className="text-amber-600 font-semibold"
                                          onClick={() => roleMutation.mutate({ id: u.id, role: "user" })}
                                          disabled={u.id === adminProfile.id}
                                        >
                                          <ShieldMinus className="w-4 h-4 mr-2" />
                                          Remover privilégios
                                        </DropdownMenuItem>
                                      </div>
                                    </TooltipTrigger>
                                    {u.id === adminProfile.id && (
                                      <TooltipContent>Você não pode remover seus próprios privilégios</TooltipContent>
                                    )}
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Tabs>

      <UserDetailsSheet 
        userId={selectedUserId}
        userInitialData={usuarios?.find(u => u.id === selectedUserId)}
        isOpen={isSheetOpen} 
        onOpenChange={setIsSheetOpen} 
      />
    </div>
  );
}

